import { useEffect, useState } from 'react';
import {
  ENTITY_TYPE_LABEL,
  formatMoney,
  type Currency,
  type EntityType,
  type MyCargoRow,
} from 'shared';
import { Money } from '../money';
import { api, type MyOffer } from '../api';
import RouteLine from '../Route';
import { alert, confirm, haptic, notify } from '../telegram';
import { useLiveRefresh, useRealtime } from '../realtime';

interface Props {
  onOpen: (cargoId: string) => void;
}

type Tab = 'cargos' | 'offers';

/** Сколько заявке осталось висеть: «истекает через 8 ч» / «< 1 ч» / «просрочена». */
function expiresHint(iso: string): string | null {
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  if (ms <= 0) return 'просрочена';
  const h = Math.floor(ms / 3_600_000);
  if (h < 1) return 'истекает < 1 ч';
  if (h < 24) return `истекает через ${h} ч`;
  return `истекает через ${Math.floor(h / 24)} дн`;
}

export default function MyCargos({ onOpen }: Props) {
  const [tab, setTab] = useState<Tab>('cargos');
  const [cargos, setCargos] = useState<MyCargoRow[]>([]);
  const [offers, setOffers] = useState<MyOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      if (tab === 'cargos') setCargos((await api.myCargos()).items);
      else setOffers((await api.myOffers()).items);
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Отклик по своей заявке: тянем список заново, чтобы пересчитались
  // разброс цен и лучшее предложение — их считает сервер.
  useLiveRefresh(() => void load());

  useRealtime(
    'offer:new',
    () => {
      if (tab !== 'cargos') return;
      haptic('medium');
      void load();
    },
    [tab],
  );

  // Свой отклик приняли или отклонили — обновляем вкладку перевозчика.
  useRealtime('offer:accepted', () => { if (tab === 'offers') void load(); }, [tab]);
  useRealtime('offer:rejected', () => { if (tab === 'offers') void load(); }, [tab]);

  async function accept(
    offerId: string,
    name: string,
    price: number,
    currency: Currency,
  ) {
    // Подтверждение показывает ту же сумму и ту же валюту, что стоит в
    // отклике: расхождение здесь — прямой путь к «я соглашался на другое».
    if (!(await confirm(`Отдать груз: ${name} за ${formatMoney(price, currency)}?`))) return;

    setBusy(true);
    try {
      await api.acceptOffer(offerId);
      notify('success');
      await load();
    } catch (e) {
      notify('error');
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const withOffers = cargos.filter((c) => c.offersCount > 0);
  const waiting = cargos.filter(
    (c) => c.offersCount === 0 && (c.status === 'ACTIVE' || c.status === 'PENDING'),
  );

  // Есть ли уже что показать по текущей вкладке. `useLiveRefresh` включает
  // `loading` при каждом тихом обновлении в фоне, и без этого признака
  // список гас бы «Загрузкой…» каждую минуту и при каждом возврате в
  // приложение — не сбой, а именно так и было задумано «перечитать», но
  // выглядело как самопроизвольное мигание экрана.
  const hasData = tab === 'cargos' ? cargos.length > 0 : offers.length > 0;

  return (
    <>
      <h2>Мои заявки</h2>

      <div className="seg" data-tour="mycargos-tabs" style={{ marginBottom: 12 }}>
        <button
          type="button"
          className={tab === 'cargos' ? 'seg__btn seg__btn--on' : 'seg__btn'}
          onClick={() => { haptic(); setTab('cargos'); }}
        >
          Я заказчик
        </button>
        <button
          type="button"
          className={tab === 'offers' ? 'seg__btn seg__btn--on' : 'seg__btn'}
          onClick={() => { haptic(); setTab('offers'); }}
        >
          Мои отклики
        </button>
      </div>

      {loading && !hasData && <div className="empty">Загрузка…</div>}

      {(!loading || hasData) && tab === 'cargos' && (
        <>
          {cargos.length === 0 && (
            <div className="empty">Вы ещё не публиковали заявки.</div>
          )}

          {withOffers.length > 0 && <h3>Есть отклики ({withOffers.length})</h3>}

          {withOffers.map((c) => (
            <div key={c.id} className="card">
              <div
                className="card--tap"
                role="button"
                tabIndex={0}
                onClick={() => { haptic(); onOpen(c.id); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    haptic();
                    onOpen(c.id);
                  }
                }}
              >
                <div className="route">
                  <RouteLine from={c.fromAddress} to={c.toAddress} />
                </div>
                <div className="meta">
                  {c.title}
                  {c.distanceKm ? ` · ~${c.distanceKm} км` : ''}
                  {c.viewsCount > 0 && ` · ${c.viewsCount} смотрели`}
                </div>
                {/* Закрытая публикация — самое частое объяснение тишины
                    в откликах. Дешевле напомнить, чем разбираться потом. */}
                {c.closed && (
                  <span className="badge badge--closed">видна только группе</span>
                )}

                <div className="row" style={{ marginTop: 8 }}>
                  <div>
                    <div className="meta">Ваша ставка</div>
                    <div className="price">{<Money value={c.price} currency={c.currency} />}</div>
                  </div>
                  {c.bestOfferRub !== null && (
                    <div style={{ textAlign: 'right' }}>
                      <div className="meta">Лучшее предложение</div>
                      <div
                        className="price"
                        style={{
                          color:
                            c.bestOfferRub <= c.priceRub
                              ? 'var(--success)'
                              : 'var(--danger)',
                        }}
                      >
                        {<Money value={c.bestOffer ?? 0} currency={c.currency} />}
                      </div>
                    </div>
                  )}
                </div>

                {c.bestOfferRub !== null &&
                  c.worstOfferRub !== null &&
                  c.offersCount > 1 && (
                    <div className="meta" style={{ marginTop: 4 }}>
                      Разброс {c.offersCount} предложений:{' '}
                      {<Money value={c.bestOffer ?? 0} currency={c.currency} />} — {<Money value={c.worstOffer ?? 0} currency={c.currency} />}
                    </div>
                  )}
              </div>

              <div className="divider" />

              {c.topOffers.map((o) => (
                <div key={o.id} className="row" style={{ marginBottom: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div>
                      <strong>{o.driverName}</strong>{' '}
                      <span className="badge">
                        {ENTITY_TYPE_LABEL[o.entityType as EntityType]}
                      </span>
                      {o.verified && (
                        <span className="badge badge--verified">проверен</span>
                      )}
                    </div>
                    <div className="meta">
                      {o.driverRatingCount > 0
                        ? `★ ${o.driverRating.toFixed(1)} · ${o.driverRatingCount} оценок`
                        : 'без оценок'}
                      {` · рейсов: ${o.driverOrdersDone}`}
                    </div>
                  </div>

                  <div style={{ textAlign: 'right' }}>
                    <div className="price" style={{ fontSize: 17 }}>
                      {<Money value={o.price} currency={c.currency} />}
                    </div>
                    {o.status === 'PENDING' ? (
                      <button
                        className="inline"
                        style={{ marginTop: 4 }}
                        disabled={busy}
                        onClick={() => void accept(o.id, o.driverName, o.price, c.currency)}
                      >
                        Выбрать
                      </button>
                    ) : (
                      <span className="badge badge--direct">принят</span>
                    )}
                  </div>
                </div>
              ))}

              {c.offersCount > 3 && (
                <button className="secondary" onClick={() => onOpen(c.id)}>
                  Ещё {c.offersCount - 3} откл.
                </button>
              )}
            </div>
          ))}

          {waiting.length > 0 && <h3>Ждут откликов ({waiting.length})</h3>}

          {waiting.map((c) => (
            <div
              key={c.id}
              className="card card--tap"
              role="button"
              tabIndex={0}
              onClick={() => onOpen(c.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpen(c.id);
                }
              }}
            >
              <div className="route">
                {c.fromAddress} → {c.toAddress}
              </div>
              {c.status === 'PENDING' && (
                <span className="badge badge--warn">на модерации</span>
              )}
              {c.closed && (
                <span className="badge badge--closed">видна только группе</span>
              )}
              <div className="row" style={{ marginTop: 8 }}>
                <div className="meta">
                  Опубликовано{' '}
                  {new Date(c.createdAt).toLocaleString('ru-RU', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                  {c.status === 'ACTIVE' && expiresHint(c.expiresAt) && (
                    <> · {expiresHint(c.expiresAt)}</>
                  )}
                </div>
                <div className="price" style={{ fontSize: 17 }}>
                  {<Money value={c.price} currency={c.currency} />}
                </div>
              </div>
            </div>
          ))}
        </>
      )}

      {(!loading || hasData) && tab === 'offers' && (
        <>
          {offers.length === 0 && (
            <div className="empty">Вы пока никуда не откликались.</div>
          )}

          {offers.map((o) => (
            <div
              key={o.id}
              className="card card--tap"
              role="button"
              tabIndex={0}
              onClick={() => { haptic(); onOpen(o.cargo.id); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  haptic();
                  onOpen(o.cargo.id);
                }
              }}
            >
              <div className="route">
                <RouteLine from={o.cargo.fromAddress} to={o.cargo.toAddress} />
              </div>
              <div className="meta">{o.cargo.title}</div>

              <div className="row" style={{ marginTop: 8 }}>
                <div>
                  <div className="meta">Моя ставка</div>
                  <div className="price">{<Money value={o.price} currency={o.currency} />}</div>
                </div>
                <span
                  className={`badge ${
                    o.status === 'ACCEPTED'
                      ? 'badge--direct'
                      : o.status === 'REJECTED'
                        ? 'badge--warn'
                        : ''
                  }`}
                >
                  {o.status === 'PENDING'
                    ? 'ждёт ответа'
                    : o.status === 'ACCEPTED'
                      ? 'принят'
                      : o.status === 'REJECTED'
                        ? 'отклонён'
                        : o.status}
                </span>
              </div>
            </div>
          ))}
        </>
      )}
    </>
  );
}
